import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {environment} from 'src/environments/environment';
import {DataService} from 'src/app/services/data.service';
import {SessionService} from 'src/app/services/session.service';
import {TrustiiService} from 'src/app/services/trustii.service';
import {DialogService} from 'src/app/services/dialog.service';
import {ProgressService} from 'src/app/services/progress.service';
import {FormBuilder, FormGroup} from '@angular/forms';
import {FileService} from 'src/app/services/file.service';
import {tap, distinctUntilChanged, filter} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {ProbaModeDialogComponent} from 'src/app/shared/proba-mode/proba-mode.dialog';

@Component({
  selector: 'app-enterprise-generate-insights',
  templateUrl: './generate-insights.component.html',
  styleUrls: ['./generate-insights.component.scss']
})
export class EnterpriseGenerateInsightsComponent implements OnInit {
  form: FormGroup;
  devMode: boolean;
  inProgress = false;
  progress = 0;
  loadingMsg = 'Fetching insight details. Please wait....';
  progressMessage = 'Uploading your live data on the cloud. Please wait...';
  mode = 'class';
  file = null;

  constructor(private router: Router, private dataservice: DataService, private formBuilder: FormBuilder,
              private trustiiService: TrustiiService, private progressService: ProgressService, private sessionService: SessionService,
              public fileService: FileService, private dialogService: DialogService, private matDialog: MatDialog) {
  }

  ngOnInit() {
    console.log(this.dataservice.data.modelType);
    this.devMode = !environment.production;
    this.form = this.formBuilder.group({
      datasetFile: ['']
    });
    if (this.fileService.file !== null) {
      this.file = this.fileService.file;
      this.fileService.file = null;
      this.chooseProbaMode();
    }
  }

  generatePrediction(options) {
    if (!options.that.sessionService.canGenerateInsight(options.that.fileService.file.size)) {
      options.that.dialogService.openAlertDialog('You have reached the prediction limit', 'FORBIDDEN');
      return;
    }
    if (options.that.sessionService.willReachInsightLimit(options.that.fileService.file.size)) {
      const message = 'You will reach your prediction limit for this month, if you continue you will pay extra fees. Do you confirm ?';
      options.that.dialogService.openConfirmDialog(message).pipe(
        distinctUntilChanged(),
        filter((result: any) => result && result.data === 'confirmation'),
        tap(() => options.that.file = options.that.fileService.file),
        tap(() => options.that.fileService.file = null),
        tap(() => options.that.chooseProbaMode())
      ).subscribe();
      return;
    }
    options.that.file = options.that.fileService.file;
    options.that.fileService.file = null;
    options.that.chooseProbaMode();
  }

  chooseProbaMode() {
    if (this.dataservice.data.mlProblem === 'regression') {
      this.uploadLiveDataAndStartMLOnCloud();
      return;
    }
    this.matDialog.open(ProbaModeDialogComponent, {
      height: '200px',
      width: '600px',
    }).afterClosed().pipe(
      distinctUntilChanged(),
      tap((result) => {
        if (result && result.mode) {
          this.mode = result.mode;
        }
      }),
      tap(() => this.uploadLiveDataAndStartMLOnCloud()),
    ).subscribe();
  }

  uploadLiveDataAndStartMLOnCloud() {
    const formData = new FormData();
    formData.append('datasetFile', this.file, this.file.name);
    this.inProgress = true;
    this.progressService.progressBar.next({inProgress: true, message: this.progressMessage, progress: this.progress});

    this.trustiiService.upload(formData, 'live').subscribe(
      (res) => {
        if (res.status === 'progress') {
          this.progress = res.message;
          this.progressService.progressBar.next({inProgress: true, message: this.progressMessage, progress: this.progress});
        } else if (res.status === 'error') {
          this.retryUpload();
        } else if (res.data) {
          this.inProgress = false;
          this.progressService.progressBar.next({inProgress: false, message: this.progressMessage, progress: this.progress});
          this.handleDatasetStatus(res.data);
        }
      },
      (err) => {
        this.retryUpload();
      }
    );
  }

  handleDatasetStatus(dataset: any) {
    if (dataset.status && dataset.status === 'done') {
      this.dataservice.setData(dataset);
      this.sessionService.refreshUser();
      this.startMLOnCloud();
      return;
    }

    this.inProgress = true;
    this.progressService.progressBar.next({loading: true, message: this.loadingMsg, inProgress: this.inProgress});

    this.trustiiService.getDataset(dataset.id).subscribe(
      (res) => {
        if ('in-progress' === res.data.status) {
          setTimeout(() => {
            this.handleDatasetStatus(dataset);
          }, 10 * 1000); // poll every 10 seconds
          return;
        } else if ('done' === res.data.status) {
          this.dataservice.setData(dataset);
          this.sessionService.refreshUser();
          this.startMLOnCloud();
          return;
        } else {
          this.retryUpload();
        }
      },
      (err) => {
        this.retryUpload();
      }
    );
  }

  retryUpload() {
    this.dialogService.openConfirmDialog('Failed to upload Dataset. Retry?').pipe(
      distinctUntilChanged(),
      tap((result) => {
        if (result && result.data === 'confirmation') {
          this.uploadLiveDataAndStartMLOnCloud();
        } else {
          this.inProgress = false;
          this.progressService.progressBar.next({inProgress: false, message: this.progressMessage, progress: this.progress});
        }
      })
    ).subscribe();
  }

  startMLOnCloud() {
    this.inProgress = true;
    this.progressService.progressBar.next({loading: true, message: this.loadingMsg, inProgress: this.inProgress});
    this.trustiiService.runMLModel(this.dataservice.data.id, this.dataservice.data.modelId, this.mode).subscribe(
      (res) => {
        if ('in-progress' === res.data.status) {
          setTimeout(() => {
            this.getInsightDetails(res.data.id);
          }, 3 * 1000); // poll every 3 seconds
        }
      },
      (err) => {
        this.inProgress = false;
        this.progressService.progressBar.next({loading: false, message: this.loadingMsg, inProgress: this.inProgress});
      }
    );
  }

  getInsightDetails(insightId) {
    this.trustiiService.getInsightDetails(insightId).subscribe(
      (res) => {
        if ('in-progress' === res.data.status) {
          setTimeout(() => {
            this.getInsightDetails(insightId);
          }, 3 * 1000); // poll every 3 seconds
        } else {
          this.inProgress = false;
          this.progressService.progressBar.next({loading: false, message: this.loadingMsg, inProgress: this.inProgress});
          this.dataservice.data = {insightId};
          this.sessionService.refreshUser();
          // TODO change route
          this.router.navigateByUrl('enterprise/datasets/' + 0 + '/insights');
        }
      }, (err) => {
        this.inProgress = false;
        this.progressService.progressBar.next({loading: false, message: this.loadingMsg, inProgress: this.inProgress});
      }
    );
  }
}
